import {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { VStack, HStack, Text, Box, Input, InputGroup } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import {
  useClass,
  useSearchClassStudents,
  useUpdateClass,
  ClassParticipantType,
} from 'hooks/api/backend/managament/getClasses';
import { Table } from 'components/common/Table/Table';
import { Pagination } from 'components/common/Table/Pagination';
import { createColumns } from 'utils/tableHelper';
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  EducationIcon,
  FilesIcon,
  SearchIcon,
} from 'assets/icons/v2';
import { PageContext } from 'components/common/PageContext';
import { ParticipantData } from 'components/common/Table/types/Column';
import { ModalVariant } from 'types/Modal';
import { useModalService } from 'hooks/useModalService';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { ProtectedRoutes } from 'types/Routes';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ErrorStatusType } from 'types/StatusTypes';
import { ChipInputChips } from 'components/common/ChipInput/ChipInput/ChipInputChips';
import { DefaultCell } from 'components/common/Table/Cells';
import { getWorksheetDashboardBreadcrumbPath } from 'utils/worksheetDashboardReturnParams';

export const ClassDetail = (): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const toast = useNovaToast();
  const modalService = useModalService();
  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();

  // Check if navigation came from worksheet dashboard and get b2bSchoolId from state
  const locationState = location.state as {
    fromWorksheetDashboard?: boolean;
  };
  const fromWorksheetDashboard = locationState?.fromWorksheetDashboard;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.length >= 1 ? searchQuery : '');
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: classData, isLoading: isLoadingClass, isError: isClassError, error: classError, refetch: refetchClass } = useClass(id || '');
  const { data: searchData, isLoading: isLoadingSearch } = useSearchClassStudents({
    classId: id || '',
    searchQuery: debouncedSearchQuery,
    page: currentPage,
    enabled: !!id,
  });
  const { mutateAsync: updateClass } = useUpdateClass();

  const currentClass = classData;
  const [className, setClassName] = useState<string>(currentClass?.name || '');
  const [isTitleEditable, setIsTitleEditable] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (currentClass?.name) {
      setClassName(currentClass.name.trim());
    }
  }, [currentClass?.name]);

  useEffect(() => {
    if (currentClass) {
      const parentPath = fromWorksheetDashboard
        ? getWorksheetDashboardBreadcrumbPath()
        : ProtectedRoutes.ORG_MANAGEMENT_CLASS_DASHBOARD;
      const parentName = fromWorksheetDashboard ? 'Arbeitsblatt-Verwaltung' : 'Kursverwaltung';

      addBreadcrumbs([
        {
          path: parentPath,
          name: parentName,
        },
        {
          path: `${ProtectedRoutes.ORG_MANAGEMENT_CLASS_DETAIL}/${id}`,
          name: currentClass.name,
        },
      ]);
    }
    return () => {
      removeBreadcrumbs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass, fromWorksheetDashboard]);

  const handleAddParticipants = (): void => {
    modalService.show(ModalVariant.CLASS_PARTICIPANT_CREATION, {
      classId: currentClass?.id,
    });
  };

  const handleAddTeachers = (): void => {
    modalService.show(ModalVariant.CLASS_TEACHER_CREATION, {
      classId: currentClass?.id,
    });
  };

  const handleAddGuides = (): void => {
    modalService.show(ModalVariant.CLASS_GUIDE_CREATION, {
      classId: currentClass?.id,
    });
  };

  const handleRemoveGuide = (guideId: string, guideName: string): void => {
    modalService.show(ModalVariant.CLASS_GUIDE_REMOVAL, {
      guideId,
      guideName,
      classId: id || '',
    });
  };
  const handleRemoveParticipant = useCallback(
    (params: {
      participantId: string;
      participantName: string;
      participantType: ClassParticipantType;
    }): void => {
      modalService.show(ModalVariant.CLASS_PARTICIPANT_REMOVAL, {
        ...params,
        classId: id || '',
      });
    },
    [id, modalService]
  );

  const toggleTitleEdit = (): void => setIsTitleEditable(!isTitleEditable);

  const handleClassNameChange = (event: React.ChangeEvent<HTMLInputElement>): void =>
    setClassName(event.target.value.trim());

  const saveClassName = async (): Promise<void> => {
    if (!id || !className || className === currentClass?.name || isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    try {
      await updateClass({
        classId: id,
        name: className,
      });
      await refetchClass();
    } catch (error) {
      // Reset to original name on error
      setClassName(currentClass?.name || '');
      toast.show(toastContent.error[ErrorStatusType.SERVER_ERROR]);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleRemoveTeacher = (teacherId: string, teacherName: string): void => {
    modalService.show(ModalVariant.CLASS_TEACHER_REMOVAL, {
      teacherId,
      teacherName,
      classId: id || '',
    });
  };

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  useLayoutEffect(() => {
    if (isTitleEditable && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
    if (!isTitleEditable && titleInputRef.current) {
      titleInputRef.current.blur();
    }
  }, [isTitleEditable]);

  const participantColumns = createColumns<ParticipantData>()({
    firstName: {
      header: 'Vorname',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
    },
    lastName: {
      header: 'Nachname',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
    },
    email: {
      header: 'E-Mail',
      sortDisabled: true,
    },
    actionItem: {
      header: 'Aktion',
      sortDisabled: true,
    },
  });

  const mappedParticipants = useMemo(() => {
    const participants =
      debouncedSearchQuery && searchData ? searchData.students : currentClass?.students || [];

    return participants.map((participant) => {
      const isTeacher = currentClass?.teachers.some((t) => t.id === participant.id) || false;
      const participantType: ClassParticipantType = isTeacher
        ? ClassParticipantType.TEACHER
        : ClassParticipantType.STUDENT;
      const participantName = `${participant.firstName} ${participant.lastName}`;

      return {
        id: participant.id,
        firstName: participant.firstName,
        lastName: participant.lastName,
        email: participant.email,
        actionItem: (
          <NovaIconButton
            aria-label="löschen"
            variant="primary"
            size="s"
            padding={0}
            margin={0}
            color="icon.tertiary"
            onClick={() =>
              handleRemoveParticipant({
                participantId: participant.id,
                participantName,
                participantType,
              })
            }
            icon={DeleteIcon}
            iconProps={{ fill: 'none' }}
          />
        ),
      };
    });
  }, [
    currentClass?.students,
    currentClass?.teachers,
    debouncedSearchQuery,
    handleRemoveParticipant,
    searchData,
  ]);

  const assignedWorksheets = classData?.worksheetsDistributedCount || 0;
  const studentCount = debouncedSearchQuery
    ? searchData?.students?.length || 0
    : currentClass?.students?.length || 0;

  if (isClassError) {
    return <QueryErrorState error={classError} onRetry={() => refetchClass()} />;
  }

  if (isLoadingClass || !currentClass) {
    return <PageSpinner />;
  }

  return (
    <PageContext title="Kurs Details" hasBackButton headTitle="Kurs Details">
      <VStack
        align="left"
        bg="background.primary"
        w="full"
        gap="l"
        p="l"
        borderRadius={{ md: '2xl' }}
      >
        <HStack gap="5px">
          <Input
            readOnly={!isTitleEditable}
            value={className}
            variant="subtle"
            w="fit-content"
            textStyle="h3.medium"
            color="text.primary"
            padding="0"
            onChange={handleClassNameChange}
            backgroundColor="transparent"
            ref={titleInputRef}
            lineClamp={1}
            overflow="hidden"
            textOverflow="ellipsis"
            cursor={isTitleEditable ? 'text' : 'pointer'}
            onClick={!isTitleEditable ? toggleTitleEdit : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveClassName();
                setIsTitleEditable(false);
              } else if (e.key === 'Escape') {
                setClassName(currentClass?.name || '');
                setIsTitleEditable(false);
              }
            }}
            onBlur={() => {
              saveClassName();
              setIsTitleEditable(false);
            }}
          />
          {!isTitleEditable && (
            <Box cursor="pointer" onClick={toggleTitleEdit}>
              <NovaIcon icon={EditIcon} size="s" color="icon.tertiary" aria-hidden />
            </Box>
          )}
        </HStack>

        {/* Teachers and Assigned Info */}
        <VStack align="flex-start" gap="10px">
          <HStack gap="5px" align="center">
            <NovaIcon icon={EducationIcon} size="m" color="text.secondary" aria-hidden />
            <Text textStyle="body.s.regular" color="text.secondary">
              Lehrende:
            </Text>
            <Box display="flex" flexWrap="wrap" gap="xs" width="100%">
              <ChipInputChips
                variant="outlined"
                values={
                  currentClass?.teachers?.map((teacher) => ({
                    id: teacher.id,
                    label: `${teacher.firstName} ${teacher.lastName}`,
                  })) || []
                }
                onRemove={({ id, label, e }) => {
                  e.stopPropagation();
                  const teacher = currentClass?.teachers?.find((t) => t.id === id);
                  if (teacher) handleRemoveTeacher(teacher.id, label);
                }}
              />
              <Box
                as="button"
                onClick={handleAddTeachers}
                bg="background.canvas"
                h="28px"
                px="10px"
                py="5px"
                borderRadius="5px"
                display="flex"
                alignItems="center"
                gap="5px"
                cursor="pointer"
                _hover={{ opacity: '80' }}
                flexShrink={0}
                border="default"
                borderColor="border.accent"
              >
                <NovaIcon icon={AddIcon} size="s" color="icon.tertiary" aria-hidden />
                <Text textStyle="body.s.medium" color="text.secondary" lineHeight="1.3">
                  Hinzufügen
                </Text>
              </Box>
            </Box>
          </HStack>
          <HStack gap="5px" align="center">
            <NovaIcon icon={EducationIcon} size="m" color="grey.secondary" aria-hidden />
            <Text textStyle="body.s.regular" color="text.secondary">
              Praxisanleitende:
            </Text>
            <Box display="flex" flexWrap="wrap" gap="8px" w="100%">
              <ChipInputChips
                variant="outlined"
                values={
                  currentClass?.guides?.map((guide) => ({
                    id: guide.id,
                    label: `${guide.firstName} ${guide.lastName}`,
                  })) || []
                }
                onRemove={({ id, label, e }) => {
                  e.stopPropagation();
                  const guide = currentClass?.guides?.find((g) => g.id === id);
                  if (guide) handleRemoveGuide(guide.id, label);
                }}
              />
              <Box
                as="button"
                onClick={handleAddGuides}
                bg="bg.darken"
                h="28px"
                px="10px"
                py="5px"
                borderRadius="5px"
                display="flex"
                alignItems="center"
                gap="5px"
                cursor="pointer"
                _hover={{ opacity: '80' }}
                flexShrink={0}
                border="default"
                borderColor="border.accent"
              >
                <NovaIcon icon={AddIcon} size="s" color="icon.tertiary" aria-hidden />
                <Text textStyle="body.s.medium" color="text.secondary" lineHeight="1.3">
                  Hinzufügen
                </Text>
              </Box>
            </Box>
          </HStack>

          <HStack gap="5px">
            <NovaIcon icon={FilesIcon} size="m" color="text.secondary" aria-hidden />
            <Text textStyle="body.s.regular" color="text.secondary">
              Zugewiesen:{' '}
              <Text as="span" textStyle="body.s.medium" color="text.secondary">
                {assignedWorksheets} Arbeitsblätter
              </Text>
            </Text>
          </HStack>
        </VStack>

        {/* Class Participants Header */}
        <HStack justify="space-between" pt="l">
          <Text textStyle="body.m.medium" color="text.secondary">
            Teilnehmende ({studentCount})
          </Text>

          <Box
            as="button"
            backgroundColor="background.accent"
            color="grey.white"
            px="15px"
            py="xs"
            borderRadius="m"
            fontSize="14px"
            fontWeight="regular"
            display="flex"
            alignItems="center"
            gap="5px"
            cursor="pointer"
            _hover={{ bg: 'background.accent.hover' }}
            onClick={handleAddParticipants}
          >
            <Box as="span" fontSize="12.8px">
              +
            </Box>
            Teilnehmende hinzufügen
          </Box>
        </HStack>

        {/* Search Input */}
        <InputGroup
          startElement={<NovaIcon icon={SearchIcon} size="m" color="icon.accent" aria-hidden />}
        >
          <Input
            ref={inputRef}
            placeholder="Nachname, Vorname, E-Mail suchen"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="background.secondary"
            border="default"
            borderColor="border.accent"
            borderRadius="10px"
            h="44px"
            _placeholder={{ color: 'text.quaternary' }}
            _focus={{
              borderColor: 'border.accent.active',
              boxShadow: 'none',
            }}
          />
        </InputGroup>

        {/* Participants Table */}
        <Box>
          <Table<ParticipantData>
            columns={participantColumns}
            data={mappedParticipants}
            isDataLoading={isLoadingSearch}
          />
          {searchData && searchData.totalPages > 1 && (
            <Pagination
              totalPages={searchData.totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </Box>
      </VStack>
    </PageContext>
  );
};
